/**
 * GanttChartWidget - Production Planning Gantt Chart
 * Custom React 19 compatible implementation (no external Gantt library)
 * Displays production operations with dependencies, conflicts, and AI planning deltas
 * Supports Drag & Drop rescheduling with cascade preview (Sprint 8.3.2-8.3.4)
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Calendar,
  ZoomIn,
  ZoomOut,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  ListFilter,
  Maximize2,
  Minimize2,
  GripVertical,
  ArrowRightLeft,
  Loader2,
  X,
  GitBranch,
  Send,
  Eye,
  Undo2,
  Info,
  Package,
  User,
  Factory,
  CalendarClock,
  Pencil,
  Link2,
} from 'lucide-react';
import { useReschedulePreview, useRescheduleOperation } from '../../hooks/useSchedulingData';
import type { RescheduleResult, ConflictSeverity } from '../../types';
import type { GanttResponse, GanttOperation, GanttDependency } from '../../hooks/useGanttData';

// ============ Types ============

type ZoomLevel = 'hour' | 'day' | 'week' | 'month';

interface TimeColumn {
  date: Date;
  label: string;
  sublabel?: string;
}

// ============ Helpers ============

function formatDt(d: Date | string) {
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function getOpColor(op: GanttOperation): string {
  if (op.has_conflicts) return 'bg-red-400';
  if (op.approval_status === 'approved') return 'bg-green-500';
  if (op.approval_status === 'rejected') return 'bg-gray-400';
  if (op.order_priority === 1) return 'bg-orange-500';
  if (op.order_priority === 2) return 'bg-yellow-500';
  return 'bg-blue-500';
}

function getWeekNumber(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function generateColumns(start: Date, end: Date, zoom: ZoomLevel): TimeColumn[] {
  const cols: TimeColumn[] = [];
  const cur = new Date(start);

  if (zoom === 'hour') {
    cur.setMinutes(0, 0, 0);
    while (cur <= end) {
      cols.push({
        date: new Date(cur),
        label: cur.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        sublabel: cur.getHours() === 0
          ? cur.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })
          : undefined,
      });
      cur.setHours(cur.getHours() + 1);
    }
  } else if (zoom === 'day') {
    cur.setHours(0, 0, 0, 0);
    while (cur <= end) {
      cols.push({
        date: new Date(cur),
        label: cur.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit' }),
        sublabel: cur.getDate() === 1 || cols.length === 0
          ? cur.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
          : undefined,
      });
      cur.setDate(cur.getDate() + 1);
    }
  } else if (zoom === 'week') {
    // Align to Monday
    const day = cur.getDay();
    cur.setDate(cur.getDate() - (day === 0 ? 6 : day - 1));
    cur.setHours(0, 0, 0, 0);
    while (cur <= end) {
      cols.push({
        date: new Date(cur),
        label: `W${getWeekNumber(cur)}`,
        sublabel: cur.getDate() <= 7 || cols.length === 0
          ? cur.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
          : undefined,
      });
      cur.setDate(cur.getDate() + 7);
    }
  } else {
    cur.setDate(1);
    cur.setHours(0, 0, 0, 0);
    while (cur <= end) {
      cols.push({
        date: new Date(cur),
        label: cur.toLocaleDateString('en-GB', { month: 'short' }),
        sublabel: cur.getMonth() === 0 || cols.length === 0
          ? String(cur.getFullYear())
          : undefined,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
  }
  return cols;
}

function getColumnWidthPx(zoom: ZoomLevel): number {
  switch (zoom) {
    case 'hour': return 50;
    case 'day': return 40;
    case 'week': return 80;
    case 'month': return 70;
  }
}

function getColumnDurationMs(zoom: ZoomLevel): number {
  switch (zoom) {
    case 'hour': return 3600000;
    case 'day': return 86400000;
    case 'week': return 604800000;
    case 'month': return 2592000000; // ~30 days
  }
}

/** Format Date to datetime-local input value (YYYY-MM-DDTHH:mm) */
function toDatetimeLocal(d: Date | string): string {
  const dt = new Date(d);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

// ============ Operation Detail Panel (readonly, non-D&D double-click) ============

interface OperationDetailPanelProps {
  operation: GanttOperation;
  onClose: () => void;
}

function OperationDetailPanel({ operation: op, onClose }: OperationDetailPanelProps) {
  return (
    <div className="absolute right-0 top-0 bottom-0 w-[380px] bg-white border-l border-gray-200 shadow-xl z-20 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-[#45bdd6]" />
          <span className="font-semibold text-sm text-gray-900">Operation Detail</span>
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        {/* Order Info */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-gray-400" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Production Order</span>
          </div>
          <p className="font-mono font-semibold text-base text-gray-900">{op.order_number}</p>
          <p className="text-sm text-gray-600 mt-0.5">Op {op.operation_sequence}: {op.description || '—'}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              op.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
              op.approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-600'
            }`}>{op.approval_status}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              op.order_priority === 1 ? 'bg-orange-100 text-orange-700' :
              op.order_priority === 2 ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-600'
            }`}>Priority {op.order_priority}</span>
            {op.has_conflicts && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">Conflict</span>
            )}
            {op.work_center_changed && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">WC Changed</span>
            )}
          </div>
        </div>

        {/* Item */}
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Item</p>
          <p className="font-mono text-sm font-medium text-gray-900">{op.item_code}</p>
          <p className="text-xs text-gray-500">{op.item_description}</p>
        </div>

        {/* Work Center */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <Factory className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Work Center</span>
          </div>
          <p className="text-sm font-medium text-gray-900">{op.work_center_code || '—'} {op.work_center_name ? `— ${op.work_center_name}` : ''}</p>
          {op.original_work_center_code && op.original_work_center_code !== op.work_center_code && (
            <p className="text-xs text-amber-600 mt-0.5">Original: {op.original_work_center_code}</p>
          )}
          {op.employee_name && (
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
              <User className="w-3 h-3" /> {op.employee_name}
            </div>
          )}
        </div>

        {/* Timing */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Scheduling</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-gray-400 mb-0.5">Planned Start</p>
              <p className="text-xs font-mono font-medium text-gray-900">{formatDt(op.planned_start)}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 mb-0.5">Planned End</p>
              <p className="text-xs font-mono font-medium text-gray-900">{formatDt(op.planned_end)}</p>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
            <span>Duration: <span className="font-medium text-gray-700">{op.duration_hours.toFixed(1)}h</span></span>
            {op.start_delta_hours !== null && op.start_delta_hours !== 0 && (
              <span className={op.start_delta_hours > 0 ? 'text-amber-600' : 'text-green-600'}>
                Delta: {op.start_delta_hours > 0 ? '+' : ''}{op.start_delta_hours.toFixed(1)}h
              </span>
            )}
          </div>
          {op.erp_planned_start && (
            <div className="mt-2 pt-2 border-t border-gray-50">
              <p className="text-[10px] text-gray-400 mb-0.5">Original ERP Times</p>
              <p className="text-xs font-mono text-gray-500">{formatDt(op.erp_planned_start)} — {op.erp_planned_end ? formatDt(op.erp_planned_end) : '—'}</p>
            </div>
          )}
        </div>

        {/* Customer & Deadline */}
        <div className="px-4 py-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-gray-400 mb-0.5">Customer</p>
              <p className="text-sm text-gray-900">{op.customer || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 mb-0.5">Required Date</p>
              <p className="text-xs font-mono text-gray-900">{op.required_date ? formatDt(op.required_date) : '—'}</p>
            </div>
          </div>
          <div className="mt-2">
            <p className="text-[10px] text-gray-400 mb-0.5">Order Status</p>
            <p className="text-sm text-gray-700">{op.order_status}</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
        <button onClick={onClose}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          Close
        </button>
      </div>
    </div>
  );
}

// ============ Reschedule Preview Panel ============

interface ReschedulePreviewPanelProps {
  operation: GanttOperation;
  newStart: Date;
  newEnd: Date;
  onTimeChange: (start: Date, end: Date) => void;
  preview: RescheduleResult | null;
  isLoadingPreview: boolean;
  previewError: string | null;
  isApplying: boolean;
  cascade: boolean;
  setCascade: (v: boolean) => void;
  writeBack: boolean;
  setWriteBack: (v: boolean) => void;
  onApply: () => void;
  onCancel: () => void;
}

function ReschedulePreviewPanel({
  operation, newStart, newEnd, onTimeChange, preview, isLoadingPreview, previewError, isApplying,
  cascade, setCascade, writeBack, setWriteBack, onApply, onCancel,
}: ReschedulePreviewPanelProps) {
  const conflictColor = (severity: ConflictSeverity) => {
    if (severity === 'error') return 'bg-red-100 text-red-800 border-red-200';
    if (severity === 'warning') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  return (
    <div className="absolute right-0 top-0 bottom-0 w-[380px] bg-white border-l border-gray-200 shadow-xl z-20 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4 text-[#45bdd6]" />
          <span className="font-semibold text-sm text-gray-900">Reschedule</span>
        </div>
        <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <p className="text-xs text-gray-500 mb-1">Operation</p>
        <p className="font-mono font-semibold text-sm text-gray-900">
          {operation.order_number}-{operation.operation_sequence}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {operation.item_code} &middot; {operation.work_center_code}
        </p>
      </div>

      <div className="px-4 py-3 border-b border-gray-100">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Before</p>
            <p className="text-xs font-mono text-gray-600">{formatDt(operation.planned_start)}</p>
            <p className="text-xs font-mono text-gray-600">{formatDt(operation.planned_end)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#45bdd6] font-semibold mb-1 flex items-center gap-1">
              After <Pencil className="w-2.5 h-2.5" />
            </p>
            <input
              type="datetime-local"
              value={toDatetimeLocal(newStart)}
              onChange={(e) => {
                const ns = new Date(e.target.value);
                if (!isNaN(ns.getTime())) {
                  const durationMs = newEnd.getTime() - newStart.getTime();
                  onTimeChange(ns, new Date(ns.getTime() + durationMs));
                }
              }}
              className="w-full text-xs font-mono font-semibold text-gray-900 border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
            />
            <input
              type="datetime-local"
              value={toDatetimeLocal(newEnd)}
              onChange={(e) => {
                const ne = new Date(e.target.value);
                if (!isNaN(ne.getTime())) {
                  onTimeChange(newStart, ne);
                }
              }}
              className="w-full mt-1 text-xs font-mono font-semibold text-gray-900 border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#45bdd6] focus:border-[#45bdd6]"
            />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-gray-100 space-y-2">
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" checked={cascade} onChange={(e) => setCascade(e.target.checked)}
            className="w-3.5 h-3.5 text-[#45bdd6] border-gray-300 rounded focus:ring-[#45bdd6]" />
          <GitBranch className="w-3.5 h-3.5 text-gray-400" />
          Cascade — reschedule dependent operations
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" checked={writeBack} onChange={(e) => setWriteBack(e.target.checked)}
            className="w-3.5 h-3.5 text-[#45bdd6] border-gray-300 rounded focus:ring-[#45bdd6]" />
          <Send className="w-3.5 h-3.5 text-gray-400" />
          Write-Back to ERP
        </label>
      </div>

      <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
        {isLoadingPreview ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#45bdd6]" />
            <span className="ml-2 text-sm text-gray-500">Calculating preview...</span>
          </div>
        ) : previewError ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-red-500">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm text-center px-2">Preview not available</span>
            <span className="text-xs text-gray-400 text-center px-2">{previewError}</span>
          </div>
        ) : preview ? (
          <>
            {(preview.conflicts?.length ?? 0) > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
                  Conflicte ({preview.conflicts.length})
                </p>
                <div className="space-y-1.5">
                  {preview.conflicts.map((c, i) => (
                    <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg border ${conflictColor(c.severity)}`}>
                      <span className="font-semibold capitalize">{c.severity}:</span> {c.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(preview.validation_warnings?.length ?? 0) > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
                  Warnings ({preview.validation_warnings.length})
                </p>
                <div className="space-y-1">
                  {preview.validation_warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg border border-amber-200">{w}</p>
                  ))}
                </div>
              </div>
            )}
            {(preview.rescheduled_operations?.length ?? 0) > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
                  Affected Operations ({preview.rescheduled_operations.length})
                </p>
                <div className="space-y-1">
                  {preview.rescheduled_operations.map((op) => (
                    <div key={op.operation_id}
                      className={`text-xs px-2.5 py-2 rounded-lg border ${op.is_primary ? 'bg-cyan-50 border-cyan-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold">{op.erp_order_number}-{op.sequence}</span>
                        {op.is_cascade && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Cascade</span>}
                        {op.is_primary && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 font-medium">Primary</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-gray-500">
                        <span>{op.old_start ? formatDt(op.old_start) : '—'}</span>
                        <span className="text-gray-300">&rarr;</span>
                        <span className="text-gray-900 font-medium">{op.new_start ? formatDt(op.new_start) : '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(preview.conflicts?.length ?? 0) === 0 && (preview.validation_warnings?.length ?? 0) === 0 && (
              <div className="flex items-center gap-2 py-3 text-green-700">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">No conflicts detected</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Eye className="w-5 h-5 mr-2" />
            <span className="text-sm">Waiting for preview...</span>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center gap-2">
        <button onClick={onCancel}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <Undo2 className="w-3.5 h-3.5" /> Cancel
        </button>
        <button onClick={onApply}
          disabled={isApplying || isLoadingPreview || (preview?.conflicts?.some(c => c.severity === 'error') ?? false)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-white bg-[#45bdd6] rounded-lg hover:bg-[#3ba8c0] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
          {isApplying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
          Apply
        </button>
      </div>
    </div>
  );
}

// ============ Gantt Bar (draggable) ============

interface GanttBarProps {
  op: GanttOperation;
  left: number;
  width: number;
  rowHeight: number;
  draggable: boolean;
  onClick?: (op: GanttOperation) => void;
  onDoubleClick?: (op: GanttOperation) => void;
  onDragEnd?: (op: GanttOperation, deltaMs: number) => void;
}

function GanttBar({ op, left, width, rowHeight, draggable, onClick, onDoubleClick, onDragEnd }: GanttBarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; origLeft: number } | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!draggable) return;
    e.preventDefault();
    e.stopPropagation();
    dragStart.current = { x: e.clientX, origLeft: left };
    setDragOffset(0);

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragStart.current) return;
      setDragOffset(ev.clientX - dragStart.current.x);
    };

    const handleMouseUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (!dragStart.current) return;
      const delta = ev.clientX - dragStart.current.x;
      dragStart.current = null;
      setDragOffset(0);
      if (Math.abs(delta) > 5 && onDragEnd) {
        try {
          onDragEnd(op, delta);
        } catch (err) {
          console.error('[GanttBar] onDragEnd error:', err);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [draggable, left, op, onDragEnd]);

  const barColor = getOpColor(op);
  const barLeft = left + dragOffset;
  const barW = Math.max(width, 6);

  return (
    <div
      ref={ref}
      className={`absolute top-1 rounded-sm shadow-sm flex items-center overflow-hidden transition-shadow
        ${barColor} ${draggable ? 'cursor-grab hover:shadow-md active:cursor-grabbing' : 'cursor-pointer'}
        ${dragOffset !== 0 ? 'opacity-80 shadow-lg ring-2 ring-cyan-400' : ''}`}
      style={{ left: barLeft, width: barW, height: rowHeight - 8 }}
      title={`${op.order_number}-${op.operation_sequence} | ${op.item_code}\n${op.work_center_code} | ${formatDt(op.planned_start)} — ${formatDt(op.planned_end)}`}
      onMouseDown={handleMouseDown}
      onClick={() => !draggable && onClick?.(op)}
      onDoubleClick={() => onDoubleClick?.(op)}
    >
      <span className="text-[10px] text-white font-medium px-1.5 truncate leading-none">
        {barW > 50 ? `${op.order_number}-${op.operation_sequence}` : ''}
      </span>
    </div>
  );
}

// ============ Dependency Lines SVG Overlay ============

interface DependencyOverlayProps {
  dependencies: GanttDependency[];
  /** Map from operation id to { x, y, w } pixel coordinates */
  opPositions: Map<string, { x: number; y: number; w: number }>;
  totalWidth: number;
  totalHeight: number;
  /** Whether to show critical path highlighting */
  highlightCritical?: boolean;
}

function DependencyOverlay({ dependencies, opPositions, totalWidth, totalHeight }: DependencyOverlayProps) {
  if (dependencies.length === 0) return null;

  const lines = useMemo(() => {
    const result: Array<{
      key: string;
      x1: number; y1: number;
      x2: number; y2: number;
      type: GanttDependency['type'];
      valid: boolean;
    }> = [];

    for (const dep of dependencies) {
      const fromPos = opPositions.get(dep.from);
      const toPos = opPositions.get(dep.to);
      if (!fromPos || !toPos) continue;

      let x1: number, y1: number, x2: number, y2: number;

      if (dep.type === 'finish_to_start') {
        // Arrow from end of "from" to start of "to"
        x1 = fromPos.x + fromPos.w;
        y1 = fromPos.y;
        x2 = toPos.x;
        y2 = toPos.y;
      } else if (dep.type === 'start_to_start') {
        x1 = fromPos.x;
        y1 = fromPos.y;
        x2 = toPos.x;
        y2 = toPos.y;
      } else {
        // finish_to_finish
        x1 = fromPos.x + fromPos.w;
        y1 = fromPos.y;
        x2 = toPos.x + toPos.w;
        y2 = toPos.y;
      }

      result.push({
        key: `${dep.from}-${dep.to}`,
        x1, y1, x2, y2,
        type: dep.type,
        valid: true,
      });
    }
    return result;
  }, [dependencies, opPositions]);

  if (lines.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none z-[5]"
      width={totalWidth}
      height={totalHeight}
      style={{ overflow: 'visible' }}
    >
      <defs>
        <marker id="dep-arrow" markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto">
          <polygon points="0,0 6,2.5 0,5" fill="#94a3b8" />
        </marker>
        <marker id="dep-arrow-critical" markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto">
          <polygon points="0,0 6,2.5 0,5" fill="#ef4444" />
        </marker>
      </defs>
      {lines.map((line) => {
        const dx = line.x2 - line.x1;
        const dy = line.y2 - line.y1;
        const isCritical = false; // Could be extended with critical path data

        // Use a routed path: horizontal out, vertical move, horizontal in
        // This avoids overlapping with bars
        const midX = line.x1 + dx * 0.5;
        const cornerR = Math.min(6, Math.abs(dy) / 2, Math.abs(dx) / 4);

        let path: string;
        if (Math.abs(dy) < 2) {
          // Same row — simple horizontal line
          path = `M ${line.x1} ${line.y1} L ${line.x2} ${line.y2}`;
        } else if (dx > 20) {
          // Forward dependency — smooth S-curve with rounded corners
          const dir = dy > 0 ? 1 : -1;
          path = `M ${line.x1} ${line.y1} `
            + `L ${midX - cornerR} ${line.y1} `
            + `Q ${midX} ${line.y1} ${midX} ${line.y1 + dir * cornerR} `
            + `L ${midX} ${line.y2 - dir * cornerR} `
            + `Q ${midX} ${line.y2} ${midX + cornerR} ${line.y2} `
            + `L ${line.x2} ${line.y2}`;
        } else {
          // Backward/tight dependency — route around with offset
          const offset = 12;
          const dir = dy > 0 ? 1 : -1;
          path = `M ${line.x1} ${line.y1} `
            + `L ${line.x1 + offset} ${line.y1} `
            + `Q ${line.x1 + offset + cornerR} ${line.y1} ${line.x1 + offset + cornerR} ${line.y1 + dir * cornerR} `
            + `L ${line.x1 + offset + cornerR} ${line.y2 - dir * cornerR} `
            + `Q ${line.x1 + offset + cornerR} ${line.y2} ${line.x1 + offset} ${line.y2} `
            + `L ${line.x2} ${line.y2}`;
        }

        return (
          <path
            key={line.key}
            d={path}
            fill="none"
            stroke={isCritical ? '#ef4444' : '#94a3b8'}
            strokeWidth={isCritical ? 2 : 1.5}
            strokeDasharray={line.type !== 'finish_to_start' ? '4 3' : undefined}
            markerEnd={isCritical ? 'url(#dep-arrow-critical)' : 'url(#dep-arrow)'}
            opacity={0.7}
          />
        );
      })}
    </svg>
  );
}

// ============ Error Boundary ============

interface EBState { hasError: boolean; error: Error | null }

class GanttErrorBoundary extends React.Component<
  { children: React.ReactNode; onReset?: () => void },
  EBState
> {
  constructor(props: { children: React.ReactNode; onReset?: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error): EBState {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
          <AlertTriangle className="w-8 h-8 text-amber-500" />
          <p className="text-sm text-gray-700 font-medium">Gantt Chart Error</p>
          <p className="text-xs text-gray-500 text-center max-w-md">{this.state.error?.message || 'Unknown error'}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); this.props.onReset?.(); }}
            className="px-4 py-2 text-xs bg-[#45bdd6] text-white rounded hover:bg-[#3ba8c0] transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============ Main Widget ============

interface GanttChartWidgetProps {
  data?: GanttResponse;
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  onOperationClick?: (op: GanttOperation) => void;
  onSelectScenario?: () => void;
  scenarioId?: string;
  height?: number | string;
}

export function GanttChartWidget(props: GanttChartWidgetProps) {
  return (
    <GanttErrorBoundary onReset={props.onRefresh}>
      <GanttChartWidgetInner {...props} />
    </GanttErrorBoundary>
  );
}

function GanttChartWidgetInner({
  data, loading, error, onRefresh, onOperationClick,
  onSelectScenario, scenarioId, height = 500,
}: GanttChartWidgetProps) {
  const ROW_HEIGHT = 32;

  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('day');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [filterWorkCenter, setFilterWorkCenter] = useState('');
  const [showDependencies, setShowDependencies] = useState(true);

  // Detail panel (readonly, double-click in non-D&D mode)
  const [detailOperation, setDetailOperation] = useState<GanttOperation | null>(null);

  // Drag & Drop Reschedule
  const [dragMode, setDragMode] = useState(false);
  const [draggedOperation, setDraggedOperation] = useState<GanttOperation | null>(null);
  const [draggedNewStart, setDraggedNewStart] = useState<Date | null>(null);
  const [draggedNewEnd, setDraggedNewEnd] = useState<Date | null>(null);
  const [showReschedulePanel, setShowReschedulePanel] = useState(false);
  const [reschedulePreview, setReschedulePreview] = useState<RescheduleResult | null>(null);
  const [cascade, setCascade] = useState(true);
  const [writeBack, setWriteBack] = useState(false);

  const previewMutation = useReschedulePreview();
  const rescheduleMutation = useRescheduleOperation();

  // Operations + filtering
  const operations = useMemo(() => {
    if (!data?.operations) return [];
    let ops = data.operations;
    if (showConflictsOnly) ops = ops.filter(o => o.has_conflicts);
    if (filterWorkCenter) ops = ops.filter(o => o.work_center_code === filterWorkCenter);
    return ops;
  }, [data, showConflictsOnly, filterWorkCenter]);

  // Group by work center
  const rows = useMemo(() => {
    const map = new Map<string, GanttOperation[]>();
    for (const op of operations) {
      const key = op.work_center_code || 'Unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(op);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [operations]);

  // Date range
  const { timeStart, timeEnd } = useMemo(() => {
    if (operations.length === 0) {
      const now = new Date();
      return { timeStart: now, timeEnd: new Date(now.getTime() + 7 * 86400000) };
    }
    const dates = operations.flatMap(o => [new Date(o.planned_start), new Date(o.planned_end)]);
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    min.setDate(min.getDate() - 1);
    max.setDate(max.getDate() + 1);
    return { timeStart: min, timeEnd: max };
  }, [operations]);

  // Columns
  const columns = useMemo(() => generateColumns(timeStart, timeEnd, zoomLevel), [timeStart, timeEnd, zoomLevel]);
  const colWidth = getColumnWidthPx(zoomLevel);
  const totalWidth = columns.length * colWidth;
  const colDuration = getColumnDurationMs(zoomLevel);

  // Work centers for filter
  const workCenters = useMemo(() => {
    if (!data?.resources) return [];
    return data.resources.map(r => ({ code: r.code, name: r.name }));
  }, [data?.resources]);

  // Pixel <-> time conversion
  const msPerPx = useMemo(() => colDuration / colWidth, [colDuration, colWidth]);

  const timeToX = useCallback((d: Date) => {
    const elapsed = d.getTime() - timeStart.getTime();
    return (elapsed / colDuration) * colWidth;
  }, [timeStart, colDuration, colWidth]);

  // Build operation position map for dependency lines
  // Maps operation id → { x (left edge px), y (vertical center px), w (width px) }
  const opPositions = useMemo(() => {
    const map = new Map<string, { x: number; y: number; w: number }>();
    let rowOffset = 0; // cumulative Y offset in px (relative to Gantt body top)
    for (const [, ops] of rows) {
      for (let idx = 0; idx < ops.length; idx++) {
        const op = ops[idx];
        const opStart = new Date(op.planned_start);
        const opEnd = new Date(op.planned_end);
        const x = timeToX(opStart);
        const w = Math.max(timeToX(opEnd) - x, 6);
        const y = rowOffset + idx * ROW_HEIGHT + ROW_HEIGHT / 2;
        map.set(op.id, { x, y, w });
      }
      rowOffset += ops.length * ROW_HEIGHT;
    }
    return map;
  }, [rows, timeToX, ROW_HEIGHT]);

  // Total height of all rows for SVG
  const totalRowHeight = useMemo(() => {
    return rows.reduce((sum, [, ops]) => sum + ops.length * ROW_HEIGHT, 0);
  }, [rows, ROW_HEIGHT]);

  // Zoom
  const zoomIn = useCallback(() => {
    const levels: ZoomLevel[] = ['month', 'week', 'day', 'hour'];
    const i = levels.indexOf(zoomLevel);
    if (i < levels.length - 1) setZoomLevel(levels[i + 1]);
  }, [zoomLevel]);
  const zoomOut = useCallback(() => {
    const levels: ZoomLevel[] = ['month', 'week', 'day', 'hour'];
    const i = levels.indexOf(zoomLevel);
    if (i > 0) setZoomLevel(levels[i - 1]);
  }, [zoomLevel]);

  // Double-click opens detail panel (works in both modes, but non-D&D shows readonly)
  const handleBarDoubleClick = useCallback((op: GanttOperation) => {
    if (!dragMode) {
      // Non-D&D: open readonly detail panel
      setDetailOperation(op);
    }
    // In D&D mode, double-click is ignored (drag interaction takes priority)
  }, [dragMode]);

  // Manual time correction in reschedule panel — updates times and re-fetches preview
  const handleTimeChange = useCallback((newStart: Date, newEnd: Date) => {
    setDraggedNewStart(newStart);
    setDraggedNewEnd(newEnd);
    setReschedulePreview(null);

    if (draggedOperation) {
      previewMutation.mutate(
        {
          operationId: draggedOperation.operation_id,
          data: {
            new_start: newStart.toISOString(),
            new_end: newEnd.toISOString(),
            cascade, validate_capacity: true, validate_material: true, dry_run: true,
          },
        },
        {
          onSuccess: (result) => setReschedulePreview(result),
          onError: () => { /* ignore */ },
        },
      );
    }
  }, [draggedOperation, cascade, previewMutation]);

  // Drag end — convert px delta to new times and trigger preview
  const handleBarDragEnd = useCallback((op: GanttOperation, pxDelta: number) => {
    try {
      const msShift = pxDelta * msPerPx;
      const newStart = new Date(new Date(op.planned_start).getTime() + msShift);
      const newEnd = new Date(new Date(op.planned_end).getTime() + msShift);

      setDraggedOperation(op);
      setDraggedNewStart(newStart);
      setDraggedNewEnd(newEnd);
      setShowReschedulePanel(true);
      setReschedulePreview(null);

      previewMutation.mutate(
        {
          operationId: op.operation_id,
          data: {
            new_start: newStart.toISOString(),
            new_end: newEnd.toISOString(),
            cascade, validate_capacity: true, validate_material: true, dry_run: true,
          },
        },
        {
          onSuccess: (result) => setReschedulePreview(result),
          onError: () => { /* preview stays null, panel shows waiting state */ },
        },
      );
    } catch (err) {
      console.error('[GanttDrag] handleBarDragEnd error:', err);
    }
  }, [msPerPx, cascade, previewMutation]);

  // Re-fetch preview when cascade changes
  useEffect(() => {
    if (showReschedulePanel && draggedOperation && draggedNewStart && draggedNewEnd) {
      setReschedulePreview(null);
      previewMutation.mutate(
        {
          operationId: draggedOperation.operation_id,
          data: {
            new_start: draggedNewStart.toISOString(),
            new_end: draggedNewEnd.toISOString(),
            cascade, validate_capacity: true, validate_material: true, dry_run: true,
          },
        },
        {
          onSuccess: (result) => setReschedulePreview(result),
          onError: () => { /* ignore */ },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cascade]);

  const handleCancelReschedule = useCallback(() => {
    setShowReschedulePanel(false);
    setDraggedOperation(null);
    setDraggedNewStart(null);
    setDraggedNewEnd(null);
    setReschedulePreview(null);
    onRefresh?.();
  }, [onRefresh]);

  const handleApplyReschedule = useCallback(() => {
    if (!draggedOperation || !draggedNewStart || !draggedNewEnd) return;
    rescheduleMutation.mutate(
      {
        operationId: draggedOperation.operation_id,
        data: {
          new_start: draggedNewStart.toISOString(),
          new_end: draggedNewEnd.toISOString(),
          cascade, validate_capacity: true, validate_material: true,
          dry_run: false, write_back_to_erp: writeBack,
        },
      },
      {
        onSuccess: () => { handleCancelReschedule(); onRefresh?.(); },
        onError: () => { /* stays in mutation error state */ },
      },
    );
  }, [draggedOperation, draggedNewStart, draggedNewEnd, cascade, writeBack, rescheduleMutation, onRefresh, handleCancelReschedule]);

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!isFullscreen) containerRef.current.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, [isFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ---- Render states ----
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-500">Loading Gantt chart...</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-red-500">
        <AlertTriangle className="w-6 h-6" />
        <span className="text-sm">{error.message}</span>
        {onRefresh && <button onClick={onRefresh} className="mt-2 px-3 py-1 text-xs bg-red-100 hover:bg-red-200 rounded">Retry</button>}
      </div>
    );
  }
  if (!scenarioId) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-500">
        <Calendar className="w-8 h-8" />
        <span className="text-sm">Select a scenario to view the Gantt chart</span>
        {onSelectScenario && <button onClick={onSelectScenario} className="mt-2 px-4 py-2 bg-[#45bdd6] text-white rounded hover:bg-[#3ba8bf] text-sm">Select Scenario</button>}
      </div>
    );
  }
  if (!data || operations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
        <Calendar className="w-8 h-8" />
        <span className="text-sm">No operations to display</span>
      </div>
    );
  }

  const stats = {
    total: data.operations.length,
    approved: data.operations.filter(o => o.approval_status === 'approved').length,
    conflicts: data.operations.filter(o => o.has_conflicts).length,
    pending: data.operations.filter(o => o.approval_status === 'pending').length,
  };

  const LABEL_WIDTH = 140;

  return (
    <div ref={containerRef} className={`flex flex-col h-full ${isFullscreen ? 'bg-white p-4' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div>
            <h3 className="font-medium text-gray-900">{data.scenario_name}</h3>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(data.planning_start).toLocaleDateString('en-GB')} - {new Date(data.planning_end).toLocaleDateString('en-GB')}
              </span>
              <span>{data.total_operations} Operations</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />{stats.approved}
            </span>
            {stats.conflicts > 0 && (
              <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />{stats.conflicts}
              </span>
            )}
            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">{stats.pending} pending</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <select value={filterWorkCenter} onChange={(e) => setFilterWorkCenter(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#45bdd6]">
            <option value="">All Work Centers</option>
            {workCenters.map(wc => <option key={wc.code} value={wc.code}>{wc.code} - {wc.name}</option>)}
          </select>
          <button onClick={() => setShowConflictsOnly(!showConflictsOnly)}
            className={`p-1.5 rounded ${showConflictsOnly ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            title={showConflictsOnly ? 'Show All' : 'Conflicts Only'}>
            <ListFilter className="w-4 h-4" />
          </button>
          <button onClick={() => setShowDependencies(!showDependencies)}
            className={`p-1.5 rounded flex items-center gap-1 text-xs font-medium transition-colors ${showDependencies ? 'bg-slate-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            title={showDependencies ? 'Hide Dependencies' : 'Show Dependencies'}>
            <Link2 className="w-4 h-4" />
            {showDependencies && <span>Deps</span>}
          </button>
          <button onClick={() => setDragMode(!dragMode)}
            className={`p-1.5 rounded flex items-center gap-1 text-xs font-medium transition-colors ${dragMode ? 'bg-[#45bdd6] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            title={dragMode ? 'Disable Drag & Drop' : 'Enable Drag & Drop'}>
            <GripVertical className="w-4 h-4" />
            {dragMode && <span>D&D</span>}
          </button>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex items-center gap-1 bg-gray-100 rounded p-0.5">
            <button onClick={zoomOut} disabled={zoomLevel === 'month'} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"><ZoomOut className="w-4 h-4" /></button>
            <span className="px-2 text-xs font-medium text-gray-600 min-w-[50px] text-center">{zoomLevel}</span>
            <button onClick={zoomIn} disabled={zoomLevel === 'hour'} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"><ZoomIn className="w-4 h-4" /></button>
          </div>
          <div className="w-px h-5 bg-gray-200" />
          <button onClick={toggleFullscreen} className="p-1.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          {onRefresh && <button onClick={onRefresh} className="p-1.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"><RefreshCw className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-1 text-xs text-gray-600">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500" /> Approved</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500" /> Pending</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-400" /> Rejected</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400" /> Conflict</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500" /> High Priority</span>
        {showDependencies && (
          <>
            <span className="w-px h-3 bg-gray-300" />
            <span className="flex items-center gap-1">
              <svg width="18" height="8"><line x1="0" y1="4" x2="14" y2="4" stroke="#94a3b8" strokeWidth="1.5" /><polygon points="14,1.5 18,4 14,6.5" fill="#94a3b8" /></svg>
              Dependency
            </span>
            <span className="flex items-center gap-1">
              <svg width="18" height="8"><line x1="0" y1="4" x2="14" y2="4" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3 2" /><polygon points="14,1.5 18,4 14,6.5" fill="#94a3b8" /></svg>
              SS/FF
            </span>
          </>
        )}
      </div>

      {dragMode && (
        <div className="flex items-center gap-2 mb-1 px-3 py-1 bg-cyan-50 border border-cyan-200 rounded text-xs text-cyan-700">
          <GripVertical className="w-3.5 h-3.5" />
          <span className="font-medium">Drag & Drop active</span>
          <span className="text-cyan-500">— Drag bars to preview rescheduling</span>
        </div>
      )}

      {/* Gantt Body */}
      <div className="flex-1 relative overflow-hidden border border-gray-200 rounded">
        <div className="flex h-full">
          {/* Row Labels */}
          <div className="flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-hidden" style={{ width: LABEL_WIDTH }}>
            <div className="h-10 border-b border-gray-200 px-2 flex items-center">
              <span className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">Work Center</span>
            </div>
            <div className="overflow-hidden">
              {rows.map(([wc, ops]) => (
                <div key={wc} style={{ height: ops.length * ROW_HEIGHT }}
                  className="flex items-start px-2 pt-2 border-b border-gray-100">
                  <span className="text-xs font-mono font-medium text-gray-700 truncate">{wc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline Area */}
          <div className="flex-1 overflow-auto" ref={scrollRef}>
            <div style={{ width: totalWidth, minWidth: '100%' }}>
              {/* Time Header */}
              <div className="h-10 border-b border-gray-200 flex sticky top-0 bg-white z-10">
                {columns.map((col, i) => (
                  <div key={i} className="border-r border-gray-100 flex flex-col items-center justify-center flex-shrink-0"
                    style={{ width: colWidth }}>
                    {col.sublabel && <span className="text-[9px] text-gray-400 font-medium leading-none">{col.sublabel}</span>}
                    <span className="text-[10px] text-gray-600 font-medium leading-tight">{col.label}</span>
                  </div>
                ))}
              </div>

              {/* Rows + Dependency Overlay wrapper */}
              <div className="relative" style={{ height: totalRowHeight }}>
                {/* Row groups */}
                {rows.map(([wc, ops]) => {
                  // Calculate this group's offset for positioning
                  return (
                    <div key={wc} className="relative border-b border-gray-100" style={{ height: ops.length * ROW_HEIGHT }}>
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex">
                        {columns.map((_, i) => (
                          <div key={i} className="border-r border-gray-50 flex-shrink-0" style={{ width: colWidth, height: '100%' }} />
                        ))}
                      </div>
                      {/* Op bars */}
                      {ops.map((op, idx) => {
                        const opStart = new Date(op.planned_start);
                        const opEnd = new Date(op.planned_end);
                        const x = timeToX(opStart);
                        const w = timeToX(opEnd) - x;
                        return (
                          <div key={op.id} className="absolute" style={{ top: idx * ROW_HEIGHT, left: 0, right: 0, height: ROW_HEIGHT }}>
                            <GanttBar
                              op={op}
                              left={x}
                              width={w}
                              rowHeight={ROW_HEIGHT}
                              draggable={dragMode}
                              onClick={onOperationClick}
                              onDoubleClick={handleBarDoubleClick}
                              onDragEnd={handleBarDragEnd}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* SVG Dependency Lines Overlay */}
                {showDependencies && (data?.dependencies?.length ?? 0) > 0 && (
                  <DependencyOverlay
                    dependencies={data!.dependencies}
                    opPositions={opPositions}
                    totalWidth={totalWidth}
                    totalHeight={totalRowHeight}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Readonly Detail Panel (double-click in non-D&D mode) */}
        {detailOperation && !showReschedulePanel && (
          <OperationDetailPanel
            operation={detailOperation}
            onClose={() => setDetailOperation(null)}
          />
        )}

        {/* Reschedule Side Panel (D&D mode) */}
        {showReschedulePanel && draggedOperation && draggedNewStart && draggedNewEnd && (
          <ReschedulePreviewPanel
            operation={draggedOperation} newStart={draggedNewStart} newEnd={draggedNewEnd}
            onTimeChange={handleTimeChange}
            preview={reschedulePreview} isLoadingPreview={previewMutation.isPending}
            previewError={previewMutation.isError ? (previewMutation.error?.message ?? 'Unknown error') : null}
            isApplying={rescheduleMutation.isPending} cascade={cascade} setCascade={setCascade}
            writeBack={writeBack} setWriteBack={setWriteBack}
            onApply={handleApplyReschedule} onCancel={handleCancelReschedule}
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
        <span>
          {operations.length} of {data.operations.length} operations
          {showConflictsOnly && ' (conflicts only)'}
          {filterWorkCenter && ` — WC: ${filterWorkCenter}`}
          {showDependencies && (data.dependencies?.length ?? 0) > 0 && ` — ${data.dependencies.length} dependencies`}
        </span>
        <span className="flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Scroll to navigate <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  );
}

export default GanttChartWidget;
